import { Link } from 'react-router-dom';
import { Banknote } from 'lucide-react';
import { formatCurrency } from '../lib/format.js';
import { SOURCE_TYPE_LABELS, SOURCE_TYPE_BADGE } from '../lib/debtHelpers.js';

/**
 * Vue inverse de DebtLotAllocations : quelles dettes financent ce lot.
 *
 * Compare le financement adossé au coût d'acquisition TTC pour répondre à
 * « ce lot est-il couvert par de la dette, et à quel taux ? ».
 */
export default function LotFinancingPanel({ financing = [], financedCents = 0, acquisitionCostCents = null }) {
  const hasCost  = acquisitionCostCents != null && acquisitionCostCents > 0;
  const coverage = hasCost ? Math.round((financedCents / acquisitionCostCents) * 100) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-4">
      <div className="flex items-center gap-2">
        <Banknote size={16} className="text-slate-500 shrink-0" aria-hidden />
        <h3 className="text-sm font-semibold text-slate-800">Financement</h3>
      </div>

      {financing.length === 0 ? (
        <p className="text-sm text-slate-500">
          Aucune dette adossée à ce lot. Pour en rattacher une, ouvrir la dette
          concernée et affecter le lot depuis le bloc « Lots financés ».
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-400">Financé par dette</p>
              <p className="text-lg font-bold tabular-nums text-slate-900">
                {formatCurrency(financedCents)}
              </p>
            </div>
            {hasCost && (
              <div>
                <p className="text-xs text-slate-400">Couverture du coût TTC</p>
                <p className={`text-lg font-bold tabular-nums ${coverage >= 100 ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {coverage} %
                </p>
                <p className="text-[11px] text-slate-400">
                  sur {formatCurrency(acquisitionCostCents)}
                </p>
              </div>
            )}
          </div>

          <ul className="divide-y divide-slate-100">
            {financing.map((f) => (
              <li key={f.allocation_id} className="py-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/dettes/${f.debt_id}`}
                    className="text-sm font-medium text-slate-800 hover:text-slate-950 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
                  >
                    {f.debt_name}
                  </Link>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${SOURCE_TYPE_BADGE[f.source_type] ?? SOURCE_TYPE_BADGE.other}`}>
                      {SOURCE_TYPE_LABELS[f.source_type] ?? f.source_type}
                    </span>
                    <span className="text-[11px] text-slate-400 tabular-nums">
                      {f.annual_rate_pct} %/an
                    </span>
                  </div>
                </div>
                <span className="text-sm tabular-nums text-slate-700 shrink-0">
                  {formatCurrency(f.allocated_cents)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
